import { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AutocompleteInput from "../components/AutocompleteInput";
import DatePickerField from "../components/DatePickerField";
import ExportMenu from "../components/ExportMenu";
import DateFilterBar, { makeInitialDateFilter, computeRange, isDateInRange } from "../components/DateFilterBar";
import { api } from "../lib/api";

const CATEGORIES = ["Publicidad", "Viajes", "Gasolina", "Oficina", "Comida", "Nómina", "Internet", "Software", "Renta", "Servicios", "Otras"];
const PAYMENT_METHODS = ["Transferencia", "Depósito", "Efectivo", "Crypto", "Otro"];
const STATUS_ORDER = ["deductible", "review", "not_deductible"];
const STATUS_LABEL = { deductible: "Deducible", review: "Revisar", not_deductible: "No deducible" };

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

const emptyForm = {
  merchant: "",
  amount: "",
  purchase_date: new Date().toISOString().slice(0, 10),
  category: "Software",
  payment_method: "Transferencia",
  supplier: "",
  note: "",
  has_invoice: false,
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState(makeInitialDateFilter);
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [paymentFilter, setPaymentFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [supplierFilter, setSupplierFilter] = useState("Todos");
  const [projectFilter, setProjectFilter] = useState("Todos");
  const [invoiceFilter, setInvoiceFilter] = useState("Todos");
  const [sort, setSort] = useState("newest");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const response = await api.get("/expenses", { withCredentials: true });
      setExpenses(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar los egresos.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get("/workspace/suppliers", { withCredentials: true })
      .then((response) => setSupplierOptions((response.data || []).map((item) => item.name).filter(Boolean)))
      .catch(() => setSupplierOptions([]));
  }, []);

  useEffect(() => {
    api.get("/projects", { withCredentials: true })
      .then((response) => setProjectOptions((response.data?.projects || []).map((p) => ({ id: p.id, name: p.name })).filter((p) => p.name)))
      .catch(() => setProjectOptions([]));
  }, []);

  const createSupplier = async (name) => {
    try {
      const response = await api.post("/workspace/suppliers", { name }, { withCredentials: true });
      const created = response.data.name;
      setSupplierOptions((current) => [...current, created]);
      setNotice(`Proveedor "${created}" creado.`);
      return created;
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos crear el proveedor.");
      return null;
    }
  };

  const dateRange = useMemo(() => computeRange(dateFilter), [dateFilter]);

  const filtered = useMemo(() => {
    return expenses
      .filter((item) => {
        const raw = item.purchase_date || item.created_at || "";
        if (!raw) return dateFilter.preset === "all";
        return isDateInRange(raw, dateRange);
      })
      .filter((item) => categoryFilter === "Todas" || (item.category || "Otras") === categoryFilter)
      .filter((item) => paymentFilter === "Todos" || (item.payment_method || "") === paymentFilter)
      .filter((item) => statusFilter === "Todos" || (item.status || "review") === statusFilter)
      .filter((item) => supplierFilter === "Todos" || (item.supplier || "") === supplierFilter)
      .filter((item) => {
        if (projectFilter === "Todos") return true;
        if (projectFilter === "__none__") return !item.project_id;
        return item.project_id === projectFilter;
      })
      .filter((item) => {
        if (invoiceFilter === "Todos") return true;
        if (invoiceFilter === "Con factura") return !!item.has_invoice;
        return !item.has_invoice;
      })
      .filter((item) => {
        if (!query.trim()) return true;
        const term = query.trim().toLowerCase();
        return (item.merchant || "").toLowerCase().includes(term)
          || (item.supplier || "").toLowerCase().includes(term)
          || (item.note || "").toLowerCase().includes(term);
      })
      .sort((a, b) => {
        const av = new Date(a.purchase_date || a.created_at || 0).getTime();
        const bv = new Date(b.purchase_date || b.created_at || 0).getTime();
        return sort === "oldest" ? av - bv : bv - av;
      });
  }, [expenses, dateFilter.preset, dateRange, categoryFilter, paymentFilter, statusFilter, supplierFilter, projectFilter, invoiceFilter, query, sort]);

  const summary = useMemo(() => {
    if (!filtered.length) return { total: 0, biggest: 0, average: 0, topCategory: "—", count: 0, deductible: 0 };
    const total = filtered.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const biggest = filtered.reduce((max, item) => Math.max(max, Number(item.amount || 0)), 0);
    const average = total / filtered.length;
    const counts = filtered.reduce((acc, item) => {
      const key = item.category || "Otras";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topCategory = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    const deductibleItems = filtered.filter((item) => (item.status || "review") === "deductible");
    return {
      total,
      biggest,
      average,
      topCategory,
      count: filtered.length,
      deductible: deductibleItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
      deductibleCount: deductibleItems.length,
    };
  }, [filtered]);

  const activeFilterCount = [
    categoryFilter !== "Todas",
    paymentFilter !== "Todos",
    statusFilter !== "Todos",
    supplierFilter !== "Todos",
    projectFilter !== "Todos",
    invoiceFilter !== "Todos",
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setCategoryFilter("Todas");
    setPaymentFilter("Todos");
    setStatusFilter("Todos");
    setSupplierFilter("Todos");
    setProjectFilter("Todos");
    setInvoiceFilter("Todos");
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (expense) => {
    setEditingId(expense.id);
    setForm({
      merchant: expense.merchant || "",
      amount: String(expense.amount ?? ""),
      purchase_date: (expense.purchase_date || expense.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      category: expense.category || "Otras",
      payment_method: expense.payment_method || "Transferencia",
      supplier: expense.supplier || "",
      note: expense.note || "",
      has_invoice: Boolean(expense.has_invoice),
    });
    setError("");
    setModalOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const amount = Number(form.amount);
    if (!form.merchant.trim() || !amount || amount <= 0) {
      setError("Concepto y monto son obligatorios.");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/expenses/${editingId}`, {
          merchant: form.merchant,
          amount,
          purchase_date: form.purchase_date,
          category: form.category,
          payment_method: form.payment_method,
          supplier: form.supplier,
          has_invoice: form.has_invoice,
          note: form.note,
        }, { withCredentials: true });
        setNotice("Egreso actualizado.");
      } else {
        const body = new FormData(event.currentTarget);
        body.set("type", "expense");
        body.set("note", form.merchant);
        body.set("details", form.note);
        body.set("amount", String(amount));
        body.set("date", form.purchase_date);
        body.set("category", form.category);
        body.set("payment_method", form.payment_method);
        body.set("supplier", form.supplier);
        body.set("has_invoice", form.has_invoice ? "true" : "false");
        await api.post("/smart-input/manual-file", body, { withCredentials: true });
        setNotice("Egreso guardado.");
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar el egreso.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/expenses/${deleteTarget.id}`, { withCredentials: true });
      setNotice("Egreso eliminado.");
      setDeleteTarget(null);
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar el egreso.");
    }
  };

  const cycleStatus = async (expense) => {
    const current = expense.status || "review";
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];
    try {
      const response = await api.put(`/expenses/${expense.id}`, { status: next }, { withCredentials: true });
      setExpenses((current) => current.map((item) => (item.id === expense.id ? response.data : item)));
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos actualizar el estado.");
    }
  };

  const scanReceipt = () => navigate("/importar");

  return (
    <AppShell>
      <div className="page expenses-page">
        <header className="expenses-head">
          <div>
            <small className="eyebrow">EGRESOS</small>
            <h1>Gastos bajo <em>control.</em></h1>
          </div>
          <div className="expenses-head-actions">
            <ExportMenu resource="expenses" testid="expenses-export" label="Exportar" />
            <button type="button" className="expenses-scan-button" data-testid="expenses-scan-button" onClick={scanReceipt} disabled={scanning}>
              <Camera size={17} />
              {scanning ? "Analizando..." : "Escanear ticket"}
            </button>
            <button type="button" className="expenses-add-button" data-testid="expenses-add-button" onClick={openCreate}>
              <Plus size={17} />
              Agregar Egreso
            </button>
          </div>
        </header>

        {notice && <p className="expenses-notice" data-testid="expenses-notice" onAnimationEnd={() => setNotice("")}>{notice}</p>}
        {error && <p className="expenses-error" data-testid="expenses-error">{error}</p>}

        <DateFilterBar value={dateFilter} onChange={setDateFilter} testid="expenses-date-filter" />

        <section className="expenses-summary" data-testid="expenses-summary">
          <article data-testid="summary-total"><small>TOTAL EGRESOS</small><strong>{money.format(summary.total)}</strong><span>{summary.count} movimiento{summary.count === 1 ? "" : "s"}</span></article>
          <article data-testid="summary-deductible"><small>DEDUCIBLES</small><strong>{money.format(summary.deductible)}</strong><span>{summary.deductibleCount || 0} confirmado{summary.deductibleCount === 1 ? "" : "s"}</span></article>
          <article data-testid="summary-biggest"><small>MAYOR EGRESO</small><strong>{money.format(summary.biggest)}</strong></article>
          <article data-testid="summary-average"><small>PROMEDIO</small><strong>{money.format(summary.average)}</strong></article>
          <article data-testid="summary-top-category"><small>CATEGORÍA MÁS USADA</small><strong>{summary.topCategory}</strong></article>
          <article data-testid="summary-count"><small>MOVIMIENTOS</small><strong>{summary.count}</strong><span>{dateRange.label || "Todo"}</span></article>
        </section>

        <section className="adv-filters" data-testid="expenses-adv-filters">
          <span className="adv-filter-group">Categoría:
            <select data-testid="expenses-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option>Todas</option>
              {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Método:
            <select data-testid="expenses-payment-filter" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option>Todos</option>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Estado:
            <select data-testid="expenses-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="deductible">Deducible</option>
              <option value="review">Revisar</option>
              <option value="not_deductible">No deducible</option>
            </select>
          </span>
          <span className="adv-filter-group">Proveedor:
            <select data-testid="expenses-supplier-filter" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
              <option>Todos</option>
              {supplierOptions.map((s) => <option key={s}>{s}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Proyecto:
            <select data-testid="expenses-project-filter" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="__none__">Sin proyecto</option>
              {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Factura:
            <select data-testid="expenses-invoice-filter" value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)}>
              <option value="Todos">Todas</option>
              <option value="Con factura">Con factura</option>
              <option value="Sin factura">Sin factura</option>
            </select>
          </span>
          {activeFilterCount > 0 && (
            <button type="button" className="adv-clear-all" onClick={clearAllFilters} data-testid="expenses-clear-filters">
              Limpiar filtros ({activeFilterCount})
            </button>
          )}
        </section>

        <section className="expenses-controls">
          <label className="expenses-search" htmlFor="expenses-search-input">
            <Search size={17} />
            <input id="expenses-search-input" data-testid="expenses-search-input" placeholder="Buscar por concepto, proveedor o nota" value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
          <select data-testid="expenses-sort" value={sort} onChange={(event) => setSort(event.target.value)} className="expenses-select">
            <option value="newest">Más recientes</option>
            <option value="oldest">Más antiguos</option>
          </select>
        </section>

        {filtered.length === 0 ? (
          <div className="expenses-empty" data-testid="expenses-empty-state">
            <img src="/mi-contador-mark.png" alt="" className="empty-brand-mark" aria-hidden="true" />
            <h2>Aún no tienes egresos registrados.</h2>
            <p>Empieza agregando tu primer gasto o escaneando un ticket.</p>
            <button type="button" onClick={openCreate} data-testid="expenses-empty-add-button">Agregar egreso</button>
          </div>
        ) : (
          <>
            <div className="expenses-table-wrap" data-testid="expenses-table-wrap">
              <table className="expenses-table" data-testid="expenses-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Categoría</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Deducible</th>
                    <th>Factura</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((expense) => (
                    <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <td>{(expense.purchase_date || expense.created_at || "").slice(0, 10)}</td>
                      <td>
                        <b>{expense.merchant}</b>
                        {expense.supplier && <small>{expense.supplier}</small>}
                      </td>
                      <td><span className="expenses-category-badge">{expense.category || "Otras"}</span></td>
                      <td><strong>{money.format(Number(expense.amount || 0))}</strong></td>
                      <td>
                        {expense.payment_method === "Efectivo" ? (
                          <span className="incomes-cash-badge" data-testid={`expense-cash-badge-${expense.id}`} title="Aparece también en Caja">
                            💵 Efectivo · en Caja
                          </span>
                        ) : (
                          expense.payment_method || "—"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          data-testid={`expense-status-${expense.id}`}
                          className={`expenses-status ${expense.status || "review"}`}
                          onClick={() => cycleStatus(expense)}
                          title="Clic para cambiar entre Deducible / Revisar / No deducible"
                        >
                          {STATUS_LABEL[expense.status || "review"]}
                        </button>
                      </td>
                      <td>{expense.has_invoice ? "Sí" : "No"}</td>
                      <td className="expenses-actions-cell">
                        <button type="button" data-testid={`expense-edit-${expense.id}`} onClick={() => openEdit(expense)} aria-label="Editar egreso"><Pencil size={15} /></button>
                        <button type="button" data-testid={`expense-delete-${expense.id}`} onClick={() => setDeleteTarget(expense)} aria-label="Eliminar egreso"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="expenses-cards" data-testid="expenses-cards">
              {filtered.map((expense) => (
                <article key={expense.id} data-testid={`expense-card-${expense.id}`}>
                  <div>
                    <b>{expense.merchant}</b>
                    <small>{(expense.purchase_date || expense.created_at || "").slice(0, 10)} · {expense.category || "Otras"}</small>
                    {expense.payment_method === "Efectivo" && (
                      <span className="incomes-cash-badge" style={{ marginTop: 6, display: "inline-flex" }} data-testid={`expense-card-cash-badge-${expense.id}`}>
                        💵 Efectivo · en Caja
                      </span>
                    )}
                    <button
                      type="button"
                      data-testid={`expense-card-status-${expense.id}`}
                      className={`expenses-status ${expense.status || "review"}`}
                      onClick={() => cycleStatus(expense)}
                      style={{ marginTop: 6 }}
                    >
                      {STATUS_LABEL[expense.status || "review"]}
                    </button>
                  </div>
                  <aside>
                    <strong>{money.format(Number(expense.amount || 0))}</strong>
                    <button type="button" data-testid={`expense-card-edit-${expense.id}`} onClick={() => openEdit(expense)} aria-label="Editar"><Pencil size={14} /></button>
                  </aside>
                </article>
              ))}
            </div>
          </>
        )}

        {modalOpen && (
          <div className="income-modal-overlay" data-testid="expense-modal">
            <form className="income-modal income-modal-form" onSubmit={submit} data-testid="expense-form">
              <button type="button" className="modal-close" data-testid="expense-modal-close" onClick={() => { setModalOpen(false); setEditingId(null); }} aria-label="Cerrar"><X /></button>
              <h2>{editingId ? "Editar Egreso" : "Añadir Egreso"}</h2>
              <label>Concepto
                <input data-testid="expense-merchant-input" value={form.merchant} onChange={(event) => setForm({ ...form, merchant: event.target.value })} required />
              </label>
              <label>Monto
                <input data-testid="expense-amount-input" type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} required />
              </label>
              <label>Fecha
                <DatePickerField value={form.purchase_date} onChange={(value) => setForm({ ...form, purchase_date: value })} testId="expense-date-input" />
              </label>
              <label>Categoría
                <select data-testid="expense-category-select" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                </select>
              </label>
              <label>Método de pago
                <select data-testid="expense-payment-select" value={form.payment_method} onChange={(event) => setForm({ ...form, payment_method: event.target.value })}>
                  {PAYMENT_METHODS.map((option) => <option key={option}>{option}</option>)}
                </select>
              </label>
              <label>Proveedor / Empresa (opcional)
                <AutocompleteInput value={form.supplier} onChange={(value) => setForm({ ...form, supplier: value })} options={supplierOptions} onCreate={createSupplier} createLabel="Crear proveedor" placeholder="Ej: Starbucks, Renta oficina..." testId="expense-supplier-input" />
              </label>
              <label>Nota (opcional)
                <textarea data-testid="expense-note-input" rows="2" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
              </label>
              <label className="expenses-inline-check">
                <input data-testid="expense-has-invoice-checkbox" type="checkbox" checked={form.has_invoice} onChange={(event) => setForm({ ...form, has_invoice: event.target.checked })} />
                <span>Tiene factura (CFDI)</span>
              </label>
              {!editingId && (
                <label>Adjuntar archivo (opcional)
                  <input data-testid="expense-file-input" name="file" type="file" accept="application/pdf,image/*" />
                </label>
              )}
              {error && <p className="billing-error" data-testid="expense-form-error">{error}</p>}
              <button type="submit" className="primary" data-testid="expense-save-button">{editingId ? "Guardar cambios" : "Guardar egreso"}</button>
            </form>
          </div>
        )}

        {deleteTarget && (
          <div className="income-modal-overlay" data-testid="expense-delete-modal">
            <div className="income-modal" style={{ maxWidth: 400 }}>
              <h2>¿Eliminar egreso?</h2>
              <p style={{ color: "#5a666b", margin: "0 0 20px" }}>Se eliminará <b>{deleteTarget.merchant}</b> por <b>{money.format(Number(deleteTarget.amount || 0))}</b>. Esta acción no se puede deshacer.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="client-cancel-button" data-testid="expense-delete-cancel" onClick={() => setDeleteTarget(null)}>Cancelar</button>
                <button type="button" className="primary" style={{ background: "#d7565d" }} data-testid="expense-delete-confirm" onClick={confirmDelete}>Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
